// src/components/ui/Sidebar.jsx
import dayjs from 'lib/dayjs';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import useUser from 'hooks/useUser';
import useUnplannedActionCount from 'hooks/useUnplannedActionCount';
import UpdatePasswordModal from 'pages/employee-profile/components/UpdatePasswordModal';
import { useUpdatePasswordMutation } from 'redux/features/users/userApi';
import { toast } from 'react-toastify';
import LogModal from '../LogModal';
import Icon from '../AppIcon';

const Sidebar = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const user = useUser();
  const userRole = user?.role?.toLowerCase();
  const unplannedActionCount = useUnplannedActionCount(user?.id, userRole !== 'employee');
  const hasSettingsPermission =
    (user?.permissions?.ipManagement && user.permissions.ipManagement !== 'none') ||
    (user?.permissions?.departmentManagement && user.permissions.departmentManagement !== 'none');
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [_updatePassword] = useUpdatePasswordMutation();

  // Handle mobile sidebar toggle
  useEffect(() => {
    const handleLogoClick = () => {
      setIsMobileOpen(!isMobileOpen);
    };

    // Listen for custom logo click events
    window.addEventListener('logoClick', handleLogoClick);

    // Close mobile sidebar when route changes
    const handleRouteChange = () => {
      setIsMobileOpen(false);
    };

    window.addEventListener('routeChange', handleRouteChange);

    return () => {
      window.removeEventListener('logoClick', handleLogoClick);
      window.removeEventListener('routeChange', handleRouteChange);
    };
  }, [isMobileOpen]);

  // Close mobile sidebar when clicking outside
  const handleOverlayClick = () => {
    setIsMobileOpen(false);
  };

  const handleUpdatePassword = async (formData) => {
    try {
      const response = await _updatePassword({ payload: formData, id: user?.id }).unwrap();
      if (response.message) {
        toast.success('Password updated successfully');
        setShowPasswordModal(false);
        return true;
      }
    } catch {
      return false;
    }
  };

  // Employee navigation items
  const employeeNavigationItems = [
    {
      label: 'Dashboard',
      path: '/dashboard-overview',
      icon: 'LayoutDashboard',
      tooltip: 'Time tracking and activity overview',
    },

    {
      label: 'Profile',
      path: '/employee-profile',
      icon: 'User',
      tooltip: 'View your profile',
    },
    {
      label: 'Calendar',
      path: '/holiday-management-employee-view',
      icon: 'Calendar',
      tooltip: 'View holidays and events',
    },
    {
      label: 'Requests',
      path: '/employee-requests',
      icon: 'GitPullRequestDraft',
      tooltip: 'Manage employee leave requests',
      count: unplannedActionCount,
    },
    {
      label: 'Company Rules',
      path: '/employee-rules',
      icon: 'BookOpenText',
      tooltip: 'Manage employee leave requests',
      isRead: user?.isRead,
    },
  ];

  // Admin navigation items
  const adminNavigationItems = [
    {
      label: 'Dashboard',
      path: '/admin-dashboard',
      icon: 'Shield',
      tooltip: 'Admin Dashboard',
    },
    {
      label: 'Employees',
      path: '/employee-management',
      icon: 'Users',
      tooltip: 'Admin employee management',
    },
    {
      label: 'Calendar',
      path: '/holiday-management-admin-view',
      icon: 'Calendar',
      tooltip: 'Manage holidays and events',
    },
    {
      label: 'Leaves',
      path: '/admin-leave-management',
      icon: 'FileText',
      tooltip: 'Manage employee leave requests',
    },
    {
      label: 'Requests',
      path: '/admin-request-management',
      icon: 'GitPullRequestDraft',
      tooltip: 'Manage employee leave requests',
    },

    {
      label: 'Company Rules',
      path: '/employee-rules',
      icon: 'BookOpenText',
      tooltip: 'Manage employee leave requests',
    },
    {
      label: 'Bonds',
      path: '/bonds',
      icon: 'FileSignature',
      tooltip: 'Manage bond information',
    },
    {
      label: 'Attendance',
      path: '/attendance-salary',
      icon: 'Calendar',
      tooltip: 'Manage attendance information',
    },
  ];

  const settingsNavItem = {
    label: 'Settings',
    path: '/admin-settings',
    icon: 'Settings',
    tooltip: 'Manage settings',
  };

  // Determine which navigation items to show based on user role
  const getNavigationItems = () => {
    if (userRole === 'admin') {
      return adminNavigationItems;
    } else if (userRole === 'employee') {
      if (hasSettingsPermission) {
        return [...employeeNavigationItems, settingsNavItem];
      }
      return employeeNavigationItems;
    }
    // Default fallback - show employee items
    return employeeNavigationItems;
  };

  const navigationItems = getNavigationItems();

  const handleNavigation = (path) => {
    navigate(path);
    // Close mobile sidebar and trigger route change event
    setIsMobileOpen(false);
    window.dispatchEvent(new CustomEvent('routeChange'));
  };

  // Detail pages sit under their menu path (/employee-management/employee-time-logs) so the parent
  // stays highlighted. A page opened from another menu can name it with
  // navigate(path, { state: { navFrom: '/menu-path' } }).
  const isActivePath = (path) => {
    const navFrom = location?.state?.navFrom;
    if (navFrom) return navFrom === path;
    return location?.pathname === path || location?.pathname?.startsWith(`${path}/`);
  };
  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={handleOverlayClick}
        />
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col fixed left-0 top-16 bottom-0 bg-surface border-r border-border z-50 w-60">
        {/* Navigation */}
        <nav className="flex-1 p-2 overflow-y-auto">
          <div className="space-y-2">
            {navigationItems?.map((item) => {
              const isActive = isActivePath(item?.path);

              return (
                <div key={item?.path} className="relative group">
                  <button
                    onClick={() => handleNavigation(item?.path)}
                    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-text-secondary hover:text-text-primary hover:bg-secondary-100'
                    }`}
                  >
                    <Icon name={item?.icon} size={20} color={isActive ? 'white' : 'currentColor'} />

                    <span className="font-medium text-sm">{item?.label}</span>

                    {item?.count > 0 && (
                      <span
                        className={`ml-auto inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                          isActive ? 'bg-white text-primary' : 'bg-red-100 text-red-700'
                        }`}
                        title="Unplanned leaves that need your action"
                      >
                        {item.count}
                      </span>
                    )}

                    {item?.isRead === false && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700 border border-green-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-green-500"></span>
                        New
                      </span>
                    )}

                    {isActive && item?.isRead !== false && (
                      <div className="ml-auto w-2 h-2 bg-white rounded-full" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-border space-y-3">
          {userRole === 'admin' ? (
            <>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="w-full flex items-center justify-center space-x-2 py-2 px-3 bg-white hover:bg-primary-50 text-text-secondary hover:text-primary rounded-lg transition-all duration-200 border border-border/50 hover:border-primary/30 shadow-sm text-xs font-bold"
              >
                <Icon name="Lock" size={14} />
                <span>Update Password</span>
              </button>
              <div
                onClick={() => handleNavigation('/admin-settings')}
                className={`flex cursor-pointer items-center space-x-3 p-3 ${isActivePath('/admin-settings') ? 'bg-primary text-white ' : 'bg-secondary-50 '} rounded-lg`}
              >
                <div
                  className={`w-8 h-8 rounded-full bg-${isActivePath('/admin-settings') ? 'white' : 'primary'} flex items-center justify-center shadow-sm`}
                >
                  <Icon
                    name="Settings"
                    size={16}
                    color={isActivePath('/admin-settings') ? 'blue' : 'white'}
                  />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium ">Admin Settings</p>
                  <p className="text-xs ">Setting Management</p>
                </div>
                {isActivePath('/admin-settings') && (
                  <div className="ml-auto w-2 h-2 bg-white rounded-full" />
                )}
              </div>
            </>
          ) : (
            <>
              <div className="p-3 bg-secondary-50/50 rounded-xl border border-border/50">
                <div className="flex items-start space-x-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm border border-border/50 shrink-0">
                    <Icon name="Timer" size={20} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold text-text-primary uppercase tracking-widest mb-1">
                      Session Status
                    </p>
                    <p className="text-xs text-text-secondary leading-relaxed line-clamp-2">
                      {user?.session_status === 'ACTIVE'
                        ? 'Currently Active'
                        : user?.lastCheckOutTime
                          ? `Last checkout: ${dayjs(user?.lastCheckOutTime).fromNow()}`
                          : 'No recent activity'}
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowSessionModal(true)}
                className="w-full flex items-center justify-center space-x-2 py-2 px-3 bg-white hover:bg-primary-50 text-text-secondary hover:text-primary rounded-lg transition-all duration-200 border border-border/50 hover:border-primary/30 shadow-sm text-xs font-bold"
              >
                <Icon name="History" size={14} />
                <span>View Login History</span>
              </button>
            </>
          )}
        </div>

        <LogModal
          isOpen={showSessionModal}
          onClose={() => setShowSessionModal(false)}
          isSelf={true}
        />
      </aside>

      {/* Mobile Sidebar Drawer */}
      <aside
        className={`lg:hidden flex flex-col fixed left-0 m-0 top-16 bottom-0 bg-surface border-r border-border z-50 w-60 transition-transform duration-300 ease-in-out ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Navigation */}
        <nav className="flex-1 p-2 overflow-y-auto">
          <div className="space-y-2">
            {navigationItems?.map((item) => {
              const isActive = isActivePath(item?.path);

              return (
                <div key={item?.path} className="relative group">
                  <button
                    onClick={() => handleNavigation(item?.path)}
                    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-text-secondary hover:text-text-primary hover:bg-secondary-100'
                    }`}
                  >
                    <Icon name={item?.icon} size={20} color={isActive ? 'white' : 'currentColor'} />

                    <span className="font-medium text-sm">{item?.label}</span>

                    {item?.count > 0 && (
                      <span
                        className={`ml-auto inline-flex min-w-[20px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                          isActive ? 'bg-white text-primary' : 'bg-red-100 text-red-700'
                        }`}
                        title="Unplanned leaves that need your action"
                      >
                        {item.count}
                      </span>
                    )}
                    {item?.isRead == false && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700 border border-green-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-green-500"></span>
                        New
                      </span>
                    )}

                    {isActive && item?.isRead !== false && (
                      <div className="ml-auto w-2 h-2 bg-white rounded-full" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-border space-y-3">
          {userRole === 'admin' ? (
            <>
              <button
                onClick={() => setShowSessionModal(true)}
                className="w-full flex items-center justify-center space-x-2 py-2 px-3 bg-white hover:bg-primary-50 text-text-secondary hover:text-primary rounded-lg transition-all duration-200 border border-border/50 hover:border-primary/30 shadow-sm text-xs font-bold"
              >
                <Icon name="History" size={14} />
                <span>Login History</span>
              </button>
              <div className="flex items-center space-x-3 p-3 bg-secondary-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-sm">
                  <Icon name="Shield" size={16} color="white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-text-primary">Admin Panel</p>
                  <p className="text-xs text-text-secondary">Management dashboard</p>
                </div>
                {isActivePath('/admin-settings') && (
                  <div className="ml-auto w-2 h-2 bg-white rounded-full" />
                )}
              </div>
            </>
          ) : (
            <>
              <div className="p-3 bg-secondary-50/50 rounded-xl border border-border/50">
                <div className="flex items-start space-x-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm border border-border/50 shrink-0">
                    <Icon name="Timer" size={20} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold text-text-primary uppercase tracking-widest mb-1">
                      Session Status
                    </p>
                    <p className="text-xs text-text-secondary leading-relaxed line-clamp-2">
                      {user?.session_status === 'ACTIVE'
                        ? 'Currently Active'
                        : user?.lastCheckOutTime
                          ? `Last checkout: ${dayjs(user?.lastCheckOutTime).fromNow()}`
                          : 'No recent activity'}
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowSessionModal(true)}
                className="w-full flex items-center justify-center space-x-2 py-2 px-3 bg-white hover:bg-primary-50 text-text-secondary hover:text-primary rounded-lg transition-all duration-200 border border-border/50 hover:border-primary/30 shadow-sm text-xs font-bold"
              >
                <Icon name="History" size={14} />
                <span>Login History</span>
              </button>
            </>
          )}
        </div>
      </aside>
      <UpdatePasswordModal
        isOpen={showPasswordModal}
        onConfirm={handleUpdatePassword}
        onClose={() => setShowPasswordModal(false)}
      />
    </>
  );
};

export default Sidebar;
